import { Button } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { ModuleContainer } from '../ModuleContainer'
import { NewPaymentMethod } from '../NewPaymentMethod'
import {
  resetPreferredPaymentMethodErrors,
  setCheckoutError,
  setCheckoutStatus,
  setRecoverableFlowPageReady,
  setRecoverableFlowStatus,
  useAppDispatch,
  useAppRouter,
  useExperimentTrigger,
  useIsConsumerLendingOrder,
  useIsRecoverableFlow,
} from '../../state'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useCheckoutSizes } from '../../utils/responsive'
import { CheckoutStatus, ExperimentValue, Features, TopazError, checkoutErrors, getAddFirstPaymentMethodExperiment } from '../../model'
import { Route } from '../../config/router'
import { RecoverableTerminalErrorStatus } from '@afterpay/types'
import { TrackingEvent } from '../../model/monitoring'
import { useFlag } from '@afterpaytouch/feature'

export const AddPaymentMethodSummary: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const dispatch = useAppDispatch()
  const { instrument } = useMonitoringWithEnduringEventProperties()
  const { buttonSize } = useCheckoutSizes()
  const isRecoverableFlow = useIsRecoverableFlow()
  const isConsumerLendingOrder = useIsConsumerLendingOrder()

  const addFirstPaymentMethodExperimentFlagVariation = useFlag<ExperimentValue>(Features.CHECKOUT_ADD_FIRST_PAYMENT_METHOD, 'E')
  const addFirstPaymentMethodExperimentCustom = getAddFirstPaymentMethodExperiment(addFirstPaymentMethodExperimentFlagVariation, 'ExperimentB')
  const triggerExperiment = useExperimentTrigger(addFirstPaymentMethodExperimentCustom)

  const handleTerminalError = (reason: TopazError): void => {
    dispatch(
      setCheckoutError({
        error: checkoutErrors[reason],
        errorCode: reason,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const handleSubmit = async (): Promise<void> => {
    if (isRecoverableFlow) {
      dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
      dispatch(setRecoverableFlowPageReady(true))
    } else {
      dispatch(setCheckoutStatus(CheckoutStatus.Started))
    }
    dispatch(resetPreferredPaymentMethodErrors())
  }

  const handleContinue = instrument(TrackingEvent.PRESSED_ADD_FIRST_PAYMENT_CONTINUE, () => setFormSubmitted(!formSubmitted))

  useEffect(() => {
    if (!addFirstPaymentMethodExperimentCustom.loading && addFirstPaymentMethodExperimentCustom.active) {
      triggerExperiment()
    }
  }, [addFirstPaymentMethodExperimentCustom, triggerExperiment])

  return (
    <ModuleContainer nested>
      <NewPaymentMethod
        onSuccess={async () => await handleSubmit()}
        displayCreditCardMessage={isConsumerLendingOrder}
        formSubmitted={formSubmitted}
        onSubmit={(isSubmitting) => setIsSubmitting(isSubmitting)}
        onTerminalError={handleTerminalError}
      />
      <ModuleContainer nested padding={{ horizontal: 'XLarge', top: 'Large' }}>
        <Button
          loading={isSubmitting}
          type='submit'
          padding='Fluid'
          size={buttonSize}
          disabled={isSubmitting}
          testNameSpace='add-payment-method'
          onClick={handleContinue}
        >
          {t(`paymentMethod:addFirstPaymentMethodSummaryExperiment`)}
        </Button>
      </ModuleContainer>
      <TrackOnMount eventName={TrackingEvent.VIEWED_ADD_FIRST_PAYMENT_EXPERIMENT} eventProperties={{ variant: 'B' }} />
    </ModuleContainer>
  )
}
